import { arrayMove } from '@dnd-kit/sortable'

import { EMPTY_LIST } from '../../../../../../constants/common'
import { useAsyncCallback, useNotification } from '../../../../../../hooks'
import { reorderPhases, updatePipelineActivity } from '../../../../../Pipeline'
import { useRequestProfileContext } from '../../../../RequestProfile.hooks'
import {
  findDndIndex,
  getActivityDndId,
  getActivityDropPosition,
  getPhaseDndId,
  isActivitiesActivityType,
  isActivitiesPhaseType,
} from '../../../RequestProfileTabActivities.utils'

import { replacePhaseActivities } from './usePhaseListDragEnd.utils'

export function usePhaseListDragEnd({
  handleReload,
  isReorderingActivities,
  phases,
  setPhases,
  setReorderingActivities,
}) {
  const { handleAddErrorNotification } = useNotification()

  const { handleReloadOpenActivities } = useRequestProfileContext()

  const handleReloadPhase = useAsyncCallback(
    phaseId => {
      return handleReload({ phaseIds: [phaseId] })
    },
    [handleReload]
  )

  function handlePhaseDragEnd(active, over) {
    const oldIndex = findDndIndex({
      getDndId: getPhaseDndId,
      items: phases,
      targetDndId: active.id,
    })
    const newIndex = findDndIndex({
      getDndId: getPhaseDndId,
      items: phases,
      targetDndId: over.id,
    })

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(phases, oldIndex, newIndex)

      reorderPhases(reordered.map(phase => phase.id)).then(handleReload)
    }
  }

  async function handleActivityDragEnd(active, over) {
    const phaseId = active.data.current?.phaseId
    const phase = phases.find(p => p.id === phaseId)

    if (phase) {
      const activities = phase.activities ?? EMPTY_LIST
      const oldIndex = findDndIndex({
        getDndId: getActivityDndId,
        items: activities,
        targetDndId: active.id,
      })
      const newIndex = findDndIndex({
        getDndId: getActivityDndId,
        items: activities,
        targetDndId: over.id,
      })

      if (oldIndex !== -1 && newIndex !== -1) {
        setPhases(prevPhases =>
          replacePhaseActivities(
            prevPhases,
            phaseId,
            arrayMove(activities, oldIndex, newIndex)
          )
        )

        setReorderingActivities(true)

        try {
          await updatePipelineActivity(activities[oldIndex].id, {
            position: getActivityDropPosition({ activities, newIndex }),
          })

          handleReloadOpenActivities?.()
        } catch {
          handleAddErrorNotification('Error.CommonApiRequestFailKey')
        } finally {
          await handleReload({ phaseIds: [phaseId] })

          setReorderingActivities(false)
        }
      }
    }
  }

  function handleDragEnd({ active, over }) {
    if (over && active.id !== over.id && !isReorderingActivities) {
      const type = active.data.current?.type

      if (isActivitiesPhaseType(type)) {
        handlePhaseDragEnd(active, over)
      } else if (isActivitiesActivityType(type)) {
        handleActivityDragEnd(active, over)
      }
    }
  }

  return { handleDragEnd, handleReloadPhase }
}
